import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  OnDestroy,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { User } from 'src/app/models/user';
import { UserService } from 'src/app/services/user.service';
import { firstValueFrom } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import {
  ConfirmationModalComponent,
  ModalStandardTemplateEnum,
} from 'src/app/standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { SettingsManagerService } from 'src/app/services/settings-manager.service';
import { PanelData, TabId } from '../../settings.constants';
import {
  IntegrationsFormService,
  FormState,
  FormAction,
} from './services/integrations-form.service';

@UntilDestroy()
@Component({
  selector: 'app-user-settings-integrations',
  templateUrl: './user-settings-integrations.component.html',
  styleUrls: ['./user-settings-integrations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsIntegrationsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() PANELS!: {
    [key: string]: PanelData;
  };
  @Input() form!: UntypedFormGroup;
  @Output() formChangedEvent = new EventEmitter<boolean>();

  protected formState$ = this.integrationsFormService.getFormState$();
  protected FormState = FormState;

  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;

  private user?: User;

  protected readonly TabId = TabId;
  /** Visible tab ids in template order. Computed once when the panel opens. */
  private readonly visibleTabIds: TabId[];

  constructor(
    private userService: UserService,
    private integrationsFormService: IntegrationsFormService,
    private dialog: MatDialog,
    private settingsManagerService: SettingsManagerService,
  ) {
    this.visibleTabIds = Object.values(TabId).filter(
      (tabId) => !this.settingsManagerService.isTabHidden(tabId),
    );
  }

  /**
   * Sets the active tab on MatTabGroup by converting TabId to the index among
   * currently rendered tabs (hidden tabs are not in the MatTabGroup).
   */
  private setActiveTabIndex(tabId: TabId): void {
    if (this.tabGroup) {
      this.tabGroup.selectedIndex = this.visibleTabIds.indexOf(tabId);
    }
  }

  ngOnInit(): void {
    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user = res?.user;
      this.initForm();
    });

    this.formState$.pipe(untilDestroyed(this)).subscribe((state) => {
      this.formChangedEvent.emit(state !== FormState.Initial);
    });
  }

  ngAfterViewInit(): void {
    // Prefer API when visible; otherwise first visible Integrations tab.
    const tab = this.visibleTabIds.includes(TabId.API) ? TabId.API : this.visibleTabIds[0];
    if (tab) {
      this.setActiveTabIndex(tab);
    }
  }

  ngOnDestroy(): void {
    this.integrationsFormService.cleanup();
  }

  private initForm(): void {
    this.form.patchValue({
      ...this.user?.institution,
      settings: {
        ...(this.user?.institution?.settings ?? {}),
      },
      permissions: {
        ...(this.user?.institution?.permissions ?? {}),
      },
      ltiSettings: this.user?.institution?.ltiSettings || [],
    });
  }

  protected submitChanges(): void {
    if (this.user) {
      this.integrationsFormService.dispatchAction(FormAction.SUBMIT);
    }
  }

  protected discardChanges(): void {
    this.integrationsFormService.dispatchAction(FormAction.CANCEL);
  }

  protected async onTabClick(event: MouseEvent, newTabId: TabId): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (await this.canSwitchTab(newTabId)) {
      this.setActiveTabIndex(newTabId);
    }
  }

  private async canSwitchTab(newTabId: TabId): Promise<boolean> {
    // Get current tab from MatTabGroup (index among visible tabs only)
    const currentIndex = this.tabGroup?.selectedIndex ?? 0;
    const currentTabId = this.visibleTabIds[currentIndex];

    if (currentTabId === newTabId) {
      return false;
    }

    if (!this.integrationsFormService.hasUnsavedChanges()) {
      return true;
    }

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: { type: ModalStandardTemplateEnum.UNSAVED_CHANGES },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    (document.activeElement as HTMLElement)?.blur();

    if (result === 'confirm') {
      this.discardChanges();
      return true;
    }
    return false;
  }
}
